import React, { useState, useEffect } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useEvents } from '@/hooks/useEvents';
import EventsListCard from '../Events/EventsListCard';
import { PricingEvent } from '@/store/eventsSlice';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import EventImageViewer from '../company/EventImageViewer';
import { Badge } from "@/components/ui/badge";
import { Zap, Loader2 } from "lucide-react";
import { getEventLabelsFromEvent } from '@/utils/eventLabels';
import { formatVersionDate } from '@/utils/dateUtils';

interface PageEventViewerProps {
  slug: string;
  subslug?: string | null;
}

const PageEventViewer = ({ slug, subslug }: PageEventViewerProps) => {
  const [selectedEvent, setSelectedEvent] = useState<PricingEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get data from Redux store
  const events = useAppSelector(state => state.events.events);
  const eventsLoading = useAppSelector(state => state.events.loading);
  const quarterlyEvents = useAppSelector(state => state.quarterlyEvents.events);
  const swipefileEntries = []; // Project functionality removed
  const pages = useAppSelector(state => state.supaPages.pages);
  const companies = useAppSelector(state => state.supaCompanies.companies);

  // Use the events hook for lazy loading
  const { fetchEvents } = useEvents(false); // Disable automatic loading

  // Load events when this component mounts and companies are available
  useEffect(() => {
    if (events.length === 0 && companies.length > 0) {
      console.log('🔄 [PageEventViewer] Lazy loading events for company details');
      fetchEvents();
    }
  }, [companies.length, fetchEvents]);

  // Set loading state based on events loading state
  useEffect(() => {
    // Set a timeout to prevent flickering for quick loads
    const timer = setTimeout(() => {
      setIsLoading(eventsLoading);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [eventsLoading]);

  // Use events from the events slice if available, otherwise use quarterly events
  const sourceEvents = events.length > 0 ? events : quarterlyEvents;

  // Log the subslug we're filtering by
  console.log('🔍 [PageEventViewer] Filtering events for slug:', slug, 'and subslug:', subslug);
  
  // Filter and process events
  const processedEvents = sourceEvents
    .filter(event => {
      // Make sure event is valid and has required properties
      if (!event || typeof event !== 'object') return false;
      
      // Filter for matching slug and has changes
      const slugMatch = event.slug === slug;
      const hasChanges = event.hasChanges !== undefined ? event.hasChanges : event.has_changes;
      
      // Filter by subslug if provided
      let subslugMatch = true;
      if (subslug) {
        // Check if event has page_subslug property and it matches
        subslugMatch = event.page_subslug === subslug;
      } else {
        // If no subslug provided, only show events without a specific page_subslug
        subslugMatch = !event.page_subslug || event.page_subslug === '';
      }
      
      return slugMatch && hasChanges && subslugMatch;
    })
    .map(event => {
      // Find the company to get the logo URL
      const company = companies.find(c => c.slug === event.slug);
      
      // Fallback to page logo if company not found
      const page = pages.find(p => p.slug === event.slug);

      // Handle both event formats (from events slice or quarterly events slice)
      const changes = Array.isArray(event.changes) ? event.changes : [];
      
      // Generate description from changes
      const desc = (changes && changes.length > 0) 
        ? changes.join(' • ') 
        : 'Pricing change';

      // Convert to PricingEvent format with safe fallbacks
      const pricingEvent: PricingEvent = {
        slug: event.slug || slug,
        v1: event.v1 || '',
        v2: event.v2 || '',
        hasChanges: event.hasChanges !== undefined ? event.hasChanges : (event.has_changes || false),
        beforeUrl: event.beforeUrl || event.v1_url || '',
        afterUrl: event.afterUrl || event.v2_url || '',
        beforePIUrl: event.beforePIUrl || event.v1_pi_url || '',
        afterPIUrl: event.afterPIUrl || event.v2_pi_url || '',
        plansChanged: event.plansChanged !== undefined ? event.plansChanged : (event.plans_changed || false),
        priceIncreased: event.priceIncreased !== undefined ? event.priceIncreased : (event.price_increased || false),
        priceDecreased: event.priceDecreased !== undefined ? event.priceDecreased : (event.price_decreased || false),
        priceAvailbilityChanged: event.priceAvailbilityChanged !== undefined ? event.priceAvailbilityChanged : (event.price_availability_changed || false),
        introDiscount: event.introDiscount !== undefined ? event.introDiscount : (event.intro_discount || false),
        displayChanged: event.displayChanged !== undefined ? event.displayChanged : (event.display_changed || false),
        changes: changes,
        description: desc,
        quarter: event.quarter || '',
        year: event.quarter ? event.quarter.split('Q')[0] : '',
        logoUrl: company?.logoUrl || page?.logoUrl || '', // Add the logo URL from the company or page
        created_at: event.created_at || ''
      };
      return pricingEvent;
    });

  const handleEventToggle = async (event: PricingEvent) => {
    // This is just a placeholder - the actual toggle functionality should be implemented
    console.log('Toggle event:', event);
  };

  const handleEventClick = (event: PricingEvent) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full">
        <h3 className="text-lg font-semibold mt-9 mb-4">Pricing Events</h3>
        <div className="flex flex-col items-center justify-center w-full h-40 bg-gray-50 rounded-lg border border-gray-200">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-2" />
          <p className="text-gray-500">Loading events...</p>
        </div>
      </div>
    );
  }

  // Show empty state
  if (processedEvents.length === 0) {
    return (
      <div className="w-full">
        <h3 className="text-lg font-semibold mt-9 mb-4">Pricing Events</h3>
        <div className="flex items-center justify-center w-full h-40 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">No pricing events found for this company</p>
        </div>
      </div>
    );
  }

  // Show events
  return (
    <div className="w-full">
      <h3 className="text-lg font-semibold mt-9 mb-4">Pricing Events</h3>
      <div className="space-y-4">
        {processedEvents.map((event) => (
          <EventsListCard
            key={`${event.slug}-${event.v1 || 'unknown'}-${event.v2 || 'unknown'}`}
            event={event}
            swipefileEntry={swipefileEntries.find(entry =>
              entry && 
              entry.slug === event.slug &&
              entry.before_version === event.v1 &&
              entry.after_version === event.v2
            )}
            onToggle={handleEventToggle}
            onClick={() => handleEventClick(event)}
          />
        ))}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-[90vw] w-[1200px] h-[90vh] p-6">
          {selectedEvent && (
            <div className="flex flex-col h-full max-h-[calc(90vh-3rem)]">
              <div className="mb-6 shrink-0">
                <div className="flex items-start space-x-4">
                  <div className="relative w-12 h-12 flex-shrink-0">
                    <img
                      src={selectedEvent.logoUrl}
                      alt={`${selectedEvent.slug} logo`}
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border-2 border-white">
                      <Zap className="h-4 w-4 text-yellow-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold capitalize">
                      {selectedEvent.slug.replace(/-/g, ' ')}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedEvent.description}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Changed between {formatVersionDate(selectedEvent.v1)} and {formatVersionDate(selectedEvent.v2)}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {getEventLabelsFromEvent(selectedEvent).map((label, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className={`flex items-center gap-1 ${label.className}`}
                        >
                          {label.icon && <label.icon className="h-3 w-3" />}
                          {label.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1 min-h-0">
                <EventImageViewer company={selectedEvent} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageEventViewer;